import * as React from 'react';
import { useInterval } from 'ahooks';

export type AudioSource = HTMLAudioElement | HTMLVideoElement | MediaStream | undefined | null;

export interface Props {
  audioSource: AudioSource;
}

let windowAudioAuth = false;
document.addEventListener('click', function clickWindowAudioAuth() {
  windowAudioAuth = true;
  document.removeEventListener('click', clickWindowAudioAuth);
});
const AudioContext = window.AudioContext || (window as any).webkitAudioContext;

const analyserMap = new WeakMap();
const gainNodeMap = new WeakMap<Exclude<AudioSource, null | undefined>, GainNode>();

export const useAudioWave = function (source: AudioSource) {
  const audioAuth = React.useRef(windowAudioAuth);
  const [waveValue, setWaveVaule] = React.useState(0);
  const [start, setStart] = React.useState(false);
  const [audioSource, setSource] = React.useState(source);
  const analyserRef = React.useRef<AnalyserNode>();
  const [isRecording, setIsRecording] = React.useState<boolean>(false);
  const [maxVolume, setMaxVolume] = React.useState<number>(0);
  const DELAYTIME = 3000;
  const [streamInterval, setStreamInterval] = React.useState<number | null>(null);

  const connect = React.useCallback(() => {
    setMaxVolume(0);
    setWaveVaule(0);
    if (audioSource) {
      if (!analyserMap.has(audioSource)) {
        if (audioSource instanceof HTMLElement) {
          const ctx = new (AudioContext || windowAudioAuth)();
          const analyser = ctx.createAnalyser();
          const gainNode = ctx.createGain();
          const source = ctx.createMediaElementSource(audioSource);
          source.connect(gainNode);
          gainNode.connect(analyser);
          analyser.connect(ctx.destination);
          analyserMap.set(audioSource, analyser);
          gainNodeMap.set(audioSource, gainNode);
        } else {
          // 处理mediaStream
          const ctx = new (AudioContext || windowAudioAuth)();
          // const biquadFilter = ctx.createBiquadFilter();
          const gainNode = ctx.createGain();
          const analyser = ctx.createAnalyser();
          const delayNode = ctx.createDelay(DELAYTIME / 1000);
          delayNode.delayTime.value = DELAYTIME / 1000;
          analyser.minDecibels = -90;
          analyser.maxDecibels = -10;
          analyser.smoothingTimeConstant = 0.85;

          const source = ctx.createMediaStreamSource(audioSource);
          source?.connect(gainNode);
          // biquadFilter.connect(gainNode);
          gainNode.connect(analyser);
          analyser.connect(delayNode);
          delayNode.connect(ctx.destination);
          // biquadFilter.type = 'lowshelf';
          // biquadFilter.frequency.setTargetAtTime(1000, ctx.currentTime, 0);
          // biquadFilter.gain.setTargetAtTime(25, ctx.currentTime, 0);
          analyserMap.set(audioSource, analyser);
          gainNodeMap.set(audioSource, gainNode);
          setStreamInterval(DELAYTIME);
        }
      }
      if (analyserMap.has(audioSource)) {
        analyserRef.current = analyserMap.get(audioSource);
      }
    }
  }, [audioSource, analyserRef.current]);

  const updateVolume = React.useCallback((): void => {
    const analyser = analyserRef.current;
    if (analyser) {
      analyser.fftSize = 256;
      const bufferLengthAlt = analyser?.frequencyBinCount;
      const dataArrayAlt = new Uint8Array(bufferLengthAlt);
      analyser?.getByteFrequencyData(dataArrayAlt);
      let total = 0;
      for (let i = 0; i < 5; i++) {
        total += dataArrayAlt[i];
      }
      const value = total / (256 * 5);
      setWaveVaule(value);
      if (value > maxVolume) {
        setMaxVolume(value);
      }
    }
  }, [analyserRef.current, maxVolume]);

  const pauseStream = React.useCallback(
    (streamSource: MediaStream, endStream = false): void => {
      if (streamSource instanceof HTMLElement) {
        console.log('This function only handles stream');
        return;
      }
      const gainNode = gainNodeMap.get(streamSource);
      if (gainNode) {
        if (endStream) {
          gainNode.disconnect();
          setStreamInterval(null);
          setIsRecording(false);
        }
        gainNode.gain.value = 0;
      }
    },
    [gainNodeMap],
  );

  const startStream = React.useCallback(
    (streamSource: MediaStream, restart = false): void => {
      if (streamSource instanceof HTMLElement) {
        console.log('This function only handles stream');
        return;
      }
      const gainNode = gainNodeMap.get(streamSource);
      if (gainNode) {
        gainNode.gain.value = 1;
        if (restart) {
          const analyserNode = analyserMap.get(streamSource);
          gainNode.connect(analyserNode);
          setStreamInterval(DELAYTIME);
        }
      }
    },
    [gainNodeMap, analyserMap],
  );

  React.useEffect(() => {
    if (!audioSource) {
      setStart(false);
    } else {
      if (!audioAuth.current) return;
      connect();
      setStart(true);
    }
  }, [audioSource]);

  useInterval(
    () => {
      updateVolume();
    },
    start ? 50 : undefined,
    {
      immediate: true,
    },
  );

  useInterval(
    () => {
      if (audioSource instanceof MediaStream) {
        if (isRecording) {
          // console.log('disconnect');
          pauseStream(audioSource);
          setIsRecording(false);
        } else {
          // console.log('connect');
          startStream(audioSource);
          setMaxVolume(0);
          setIsRecording(true);
        }
      }
    },
    audioSource instanceof MediaStream ? streamInterval : null,
    {
      immediate: true,
    },
  );

  React.useEffect(() => {
    if (!audioAuth.current) {
      document.addEventListener('click', function AudioWaveClick() {
        audioAuth.current = true;
        document.removeEventListener('click', AudioWaveClick);
      });
    }
  }, []);
  return {
    waveValue,
    setSource,
    pauseStream,
    startStream,
    isRecording,
    maxVolume,
  };
};

const AudioWave: React.FC<Props> = props => {
  const { audioSource } = props;
  const { waveValue, setSource } = useAudioWave(audioSource);
  React.useEffect(() => {
    setSource(audioSource);
  }, [audioSource]);
  return (
    <div>
      <span>{waveValue}</span>
    </div>
  );
};

export default AudioWave;
